import { useState } from "react";
import { Link2, ExternalLink, X, Plus } from "lucide-react";

const normalizeUrl = (s) => {
  const t = (s || "").trim();
  return /^https?:\/\//i.test(t) ? t : `https://${t}`;
};

const looksLikeUrl = (s) => /^(https?:\/\/)?[^\s/]+\.[^\s]{2,}/i.test((s || "").trim());

/** "https://www.homedepot.com/p/…" → "homedepot.com" — default label when none is given. */
export function linkHost(url) {
  try {
    return new URL(normalizeUrl(url)).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/**
 * Reference links on a Plan line (quotes, product pages, contracts, portals).
 * Stored on the budget category as `links: [{ url, label }]`.
 */
export default function PlanLinks({ links = [], onChange }) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");

  const add = () => {
    const raw = draft.trim();
    if (!raw) return;
    if (!looksLikeUrl(raw)) {
      setError("That doesn't look like a link.");
      return;
    }
    onChange([...links, { url: normalizeUrl(raw), label: "" }]);
    setDraft("");
    setError("");
  };

  const update = (i, patch) => onChange(links.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const remove = (i) => onChange(links.filter((_, idx) => idx !== i));

  return (
    <div>
      {links.length > 0 && (
        <div className="space-y-0.5 mb-1">
          {links.map((l, i) => (
            <div key={i} className="flex items-center gap-1.5 group/link">
              <a
                href={normalizeUrl(l.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 text-indigo-400 hover:text-indigo-300"
                title={l.url}
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <input
                type="text"
                value={l.label || ""}
                onChange={(e) => update(i, { label: e.target.value })}
                placeholder={linkHost(l.url)}
                className="flex-1 min-w-0 text-xs text-gray-300 placeholder-gray-500 bg-transparent rounded px-1.5 py-0.5 border border-transparent hover:border-gray-600 outline-none focus:ring-1 focus:ring-indigo-500"
                title="Label (optional)"
              />
              {l.label && <span className="text-[10px] text-gray-600 truncate max-w-[10rem]">{linkHost(l.url)}</span>}
              <button
                onClick={() => remove(i)}
                className="p-0.5 text-gray-600 hover:text-red-400 md:opacity-0 md:group-hover/link:opacity-100 transition-opacity"
                aria-label="Remove link"
                title="Remove link"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-1.5">
        {links.length > 0 ? (
          <Plus className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
        ) : (
          <Link2 className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
        )}
        <input
          type="url"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setError(""); }}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          onBlur={() => { if (draft.trim() && looksLikeUrl(draft)) add(); }}
          placeholder="Paste a link… press Enter"
          className="flex-1 min-w-0 text-xs bg-transparent focus:outline-none py-0.5 text-gray-300 placeholder-gray-600"
        />
      </div>
      {error && <p className="text-[10px] text-red-300 mt-0.5">{error}</p>}
    </div>
  );
}
